import { Box, Flex, Icon, SystemStyleObject, Text } from '@chakra-ui/react'
import React from 'react'
import IconBlocked from '~/assets/svgs/icon-blocked.svg?react'
import IconCallStart from '~/assets/svgs/icon-call-start.svg?react'
import IconContacts from '~/assets/svgs/icon-contacts.svg?react'
import IconCopy from '~/assets/svgs/icon-copy.svg?react'
import IconEllipsis from '~/assets/svgs/icon-ellipsis.svg?react'
import IconFax from '~/assets/svgs/icon-fax.svg?react'
import IconMessaging from '~/assets/svgs/icon-messaging.svg?react'
import {
  useBlockedNumbers,
  useCanBlockNumber,
  useFindBlockedNumber,
} from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  DropdownActionButton,
  MangoButtonMenuItem,
} from '~/components/shared/Dropdown/DropdownActionButton'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { ContactsOperation } from '~/hooks/toast/eventHandlers/contactsToastEventHandler'
import { useClipboard } from '~/hooks/useClipboard'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  ContactCellStyleValues,
  useContactCellStyles,
} from '~/pages/Calls/componentStyles/CallsListItem.style'
import { ContactDetails } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'

interface ContactCellProps {
  columnStyles: SystemStyleObject
  title: string
  subtitle: string
  contact?: ContactDetails
  number: string
  isExtension?: boolean
  isHovered?: boolean
}

export const ContactCell = ({
  columnStyles,
  title,
  subtitle,
  contact,
  number,
  isExtension,
  isHovered,
}: ContactCellProps) => {
  const { emit } = useEmitterService()

  const handleSuccess = () => {
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.CONTACTS,
      operation: ContactsOperation.ContactNumberCopiedToClipboard,
    })
  }

  const handleFailure = (reason?: any) => {
    console.error(reason)
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Error,
      domain: NotificationDomain.CONTACTS,
      operation: ContactsOperation.ContactNumberCopiedToClipboard,
    })
  }

  const styles = useContactCellStyles()
  const { copyToClipboard } = useClipboard({
    onFailure: handleFailure,
    onSuccess: handleSuccess,
  })

  const { blockedNumbers, unblockNumber, blockNumberWithConfirmation } =
    useBlockedNumbers()

  const [menuOpen, setMenuOpen] = React.useState(false)
  const handleMenuOpenChange = (isOpen: boolean) => {
    setMenuOpen(isOpen)
  }

  const {
    callModal: [, setCallModalState],
    faxModal: [faxModalState, setFaxModalState],
    messagingModal: [, setMessagingModalState],
  } = useModalState()

  const handleCopyNumber = () => {
    copyToClipboard(number ?? '')
  }

  const handleCallNumber = () => {
    const callee =
      contact !== undefined && contact.defaultNumber !== undefined
        ? { contactNumber: contact.defaultNumber, contact: contact }
        : number
    setCallModalState({
      open: true,
      callee: callee,
    })
  }

  const handleMessageNumber = () => {
    setMessagingModalState({ open: true, number })
  }

  const handleSendFax = () => {
    setFaxModalState({ ...faxModalState, open: true, number })
  }

  const isMangoUser = isExtension // TODO: We need a better way to know if this is an internal user that isn't using their extension

  const buttonMenuItem = (
    value: string,
    text: string,
    icon: SVGComponent,
    handle: () => void,
  ): MangoButtonMenuItem<string> => ({
    value,
    content: (
      <MenuListItem primaryText={text} leftContent={<Icon as={icon} />} />
    ),
    onSelect: handle,
  })

  const menuItems: MangoButtonMenuItem<string>[] = isMangoUser
    ? [
        buttonMenuItem('call', 'Call Number', IconCallStart, handleCallNumber),
        buttonMenuItem('copy', 'Copy Number', IconCopy, handleCopyNumber),
      ]
    : [
        buttonMenuItem('call', 'Call Number', IconCallStart, handleCallNumber),
        buttonMenuItem(
          'message',
          'Message Number',
          IconMessaging,
          handleMessageNumber,
        ),
        buttonMenuItem('fax', 'Send Fax', IconFax, handleSendFax),
        buttonMenuItem('copy', 'Copy Number', IconCopy, handleCopyNumber),
      ]

  const canBlockNumber = useCanBlockNumber(number)
  const blockedNumber = useFindBlockedNumber(blockedNumbers, number)
  if (!isMangoUser) {
    if (blockedNumber) {
      menuItems.push(
        buttonMenuItem('unblock', 'Unblock Number', IconBlocked, () => {
          unblockNumber(blockedNumber.id, blockedNumber.phoneNumber)
        }),
      )
    } else if (canBlockNumber) {
      menuItems.push(
        buttonMenuItem('block', 'Block Number', IconBlocked, () => {
          blockNumberWithConfirmation(number)
        }),
      )
    }
  }

  const showActionButton = isHovered || menuOpen

  return (
    <Flex sx={{ ...columnStyles, ...styles.container }}>
      <Flex sx={styles.titleContainer}>
        {contact && <Icon as={IconContacts} sx={styles.titleIcon} />}
        <Text sx={styles.titleText}>{title}</Text>
      </Flex>
      <Flex sx={styles.subtitleContainer}>
        <Text sx={styles.subtitleText}>{subtitle}</Text>
        {isExtension && (
          <ExtensionBadge
            extension={number}
            variant={ExtensionBadgeVariant.LightGray}
            size={Size.sm}
          />
        )}

        <Box
          opacity={showActionButton ? '100%' : '0'}
          _hover={{ opacity: '100%' }}
        >
          <DropdownActionButton
            icon={IconEllipsis}
            variant={QuickActionButtonVariants.Default_100}
            size={Size.xs}
            tooltipText="More options"
            onMenuOpenChange={handleMenuOpenChange}
            menuOptions={{
              width: ContactCellStyleValues.menuWidth,
            }}
            items={menuItems}
          />
        </Box>
      </Flex>
    </Flex>
  )
}
